import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { ContentCopy, Warning } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "src/utils/useAuth";
import type { DecodedJWT } from "src/types/auth.types";

export function DebugPage() {
  const { isEnabled, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect } = useAuth();
  const [token, setToken] = useState<string>("");
  const [decoded, setDecoded] = useState<DecodedJWT | null>(null);
  const [idTokenClaims, setIdTokenClaims] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string>("");
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isEnabled) return;

    const fetchToken = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        setToken(accessToken);

        // Check if token is a JWT (has 3 parts: header.payload.signature)
        const parts = accessToken.split(".");
        if (parts.length !== 3) {
          setError(
            "This is an opaque token, not a JWT. To get a JWT access token, configure an API in your Auth0 dashboard and add AUTH0_AUDIENCE to your .env file."
          );
          return;
        }

        const decodedToken = jwtDecode(accessToken);
        const [header] = parts;
        setDecoded({
          header: JSON.parse(atob(header)),
          payload: decodedToken as DecodedJWT["payload"],
        });
        setError("");
      } catch (err) {
        if (err instanceof Error && err.message.includes("Invalid token")) {
          // Opaque token — informational only, user is legitimately authenticated
          setError(
            "Unable to decode token. This may be an opaque token instead of a JWT. To get a JWT, configure an API in your Auth0 dashboard and add AUTH0_AUDIENCE to your .env file."
          );
        } else if (
          err instanceof Error &&
          (err.message.includes("Missing Refresh Token") ||
            err.message.includes("login_required") ||
            err.message.includes("consent_required"))
        ) {
          // Session is broken — redirect to login
          const returnTo = window.location.pathname + window.location.search + window.location.hash;
          loginWithRedirect({ appState: { returnTo } });
        } else {
          setError(err instanceof Error ? err.message : "Failed to retrieve token");
        }
      }
    };

    const fetchIdToken = async () => {
      try {
        const claims = await getIdTokenClaims();
        if (claims) {
          setIdTokenClaims(claims);
        }
      } catch {
        // ID token is optional — ignore errors
      }
    };

    fetchToken();
    fetchIdToken();
  }, [isEnabled, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect]);

  useEffect(() => {
    if (!decoded?.payload.exp) return;

    const interval = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = decoded.payload.exp! - now;
      setTimeUntilExpiry(remaining > 0 ? remaining : 0);
    }, 1000);

    return () => clearInterval(interval);
  }, [decoded?.payload.exp]);

  const handleCopy = () => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  if (!isEnabled) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Auth0 is not enabled. Re-run the setup script to enable authentication features.
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, md: 4 }, px: { xs: 1, md: 3 } }}>
      <Stack spacing={2}>
        {/* Header */}
        <Box>
          <Typography variant="h5" gutterBottom>
            JWT Debug
          </Typography>
          <Alert severity="warning" icon={<Warning />}>
            This page displays sensitive authentication information. Do not share tokens or
            screenshots.
          </Alert>
        </Box>

        {/* Token Expiration */}
        {decoded?.payload.exp && (
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Token Status
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Expires In
                  </Typography>
                  <Typography variant="h6" color="primary" noWrap>
                    {timeUntilExpiry !== null ? formatTime(timeUntilExpiry) : "Calculating..."}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="body2" color="text.secondary">
                    Expires At
                  </Typography>
                  <Typography variant="body1" noWrap>
                    {formatTimestamp(decoded.payload.exp)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Raw Token */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Raw Token</Typography>
              <Button
                startIcon={<ContentCopy />}
                onClick={handleCopy}
                size="small"
                variant="outlined"
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </Box>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "background.default",
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: "break-all" }}>
                {token || "Loading..."}
              </Typography>
            </Paper>
          </CardContent>
        </Card>

        {/* Decoded Header */}
        {decoded && (
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Decoded Header
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: "background.default", overflow: "hidden" }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    overflow: "hidden",
                  }}
                >
                  {JSON.stringify(decoded.header, null, 2)}
                </pre>
              </Paper>
            </CardContent>
          </Card>
        )}

        {/* Decoded Payload/Claims */}
        {decoded && (
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Decoded Payload (Claims)
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: "background.default", overflow: "hidden" }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    overflow: "hidden",
                  }}
                >
                  {JSON.stringify(decoded.payload, null, 2)}
                </pre>
              </Paper>
            </CardContent>
          </Card>
        )}

        {/* ID Token Claims */}
        {idTokenClaims && (
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                ID Token Claims
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: "background.default", overflow: "hidden" }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    overflow: "hidden",
                  }}
                >
                  {JSON.stringify(idTokenClaims, null, 2)}
                </pre>
              </Paper>
            </CardContent>
          </Card>
        )}

        {/* Token Metadata */}
        {decoded?.payload && (
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Token Metadata
              </Typography>
              <Stack spacing={2}>
                {decoded.payload.iss && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Issuer (iss)
                    </Typography>
                    <Typography variant="body1">{decoded.payload.iss}</Typography>
                  </Box>
                )}
                {decoded.payload.aud && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Audience (aud)
                    </Typography>
                    <Typography variant="body1">
                      {Array.isArray(decoded.payload.aud)
                        ? decoded.payload.aud.join(", ")
                        : decoded.payload.aud}
                    </Typography>
                  </Box>
                )}
                {decoded.payload.iat && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Issued At (iat)
                    </Typography>
                    <Typography variant="body1">{formatTimestamp(decoded.payload.iat)}</Typography>
                  </Box>
                )}
                {decoded.payload.nbf && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Not Before (nbf)
                    </Typography>
                    <Typography variant="body1">{formatTimestamp(decoded.payload.nbf)}</Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
